import { useMemo } from 'react';
import Supercluster from 'supercluster';

export const MAX_ZOOM = 13;

const getBoundsFromMapResult = (mapBounds) => {
    if (!mapBounds) {
        return null;
    }

    const {
        north, east, south, west,
    } = mapBounds;

    return {
        nw: {
            lat: north,
            lng: west,
        },
        ne: {
            lat: north,
            lng: east,
        },
        se: {
            lat: south,
            lng: east,
        },
        sw: {
            lat: south,
            lng: west,
        },
    };
};

export const getObjectCoordinates = (item) => {
    const lat = item?.latitude ?? item?.location?.latitude;
    const lng = item?.longitude ?? item?.location?.longitude;

    if (!lat || !lng) {
        return null;
    };

    return { lat, lng };
};

const defaultSelector = () => true;

export const useClusters = (hits, rawBounds, zoom, selector = defaultSelector) => {
    const points = useMemo(() => {
        const groups = {};

        (hits || []).forEach((job) => {
            if (!selector(job)) {
                return;
            }

            const coords = getObjectCoordinates(job);

            if (!coords) {
                return;
            }

            const key = `${coords.lat},${coords.lng}`;

            if (!groups[key]) {
                groups[key] = {
                    key,
                    position: coords,
                    jobs: [],
                };
            }

            groups[key].jobs.push({
                ...job,
                coordinates: coords,
            });
        });

        return Object.values(groups).map((group) => ({
            type: 'Feature',
            properties: {
                cluster: false, objectID: group.key, jobs: group.jobs,
            },
            geometry: {
                type: 'Point',
                coordinates: [
                    group.position.lng,
                    group.position.lat,
                ],
            },
        }));
    }, [hits, selector]);

    const [clusterResult, superclusterResult] = useMemo(() => {
        const supercluster = new Supercluster({
            maxZoom: MAX_ZOOM,
            radius: 75,
            map: (props) => ({ sum: props.jobs.length || 0 }),
            reduce: (accumulated, props) => {
                accumulated.sum += props.sum;
            },
        });

        supercluster.load(points);

        const bounds = getBoundsFromMapResult(rawBounds);
        const clusters = supercluster.getClusters(
            [bounds?.sw.lng, bounds?.sw.lat, bounds?.ne.lng, bounds?.ne.lat],
            zoom,
        );

        return [clusters, supercluster];
    }, [points, rawBounds, zoom]);

    return [
        clusterResult,
        superclusterResult,
    ];
};